import type { Organisation, OrganisationPatch } from '@/core/api/organisation'
export type { Organisation, OrganisationPatch } from '@/core/api/organisation'
import { apiGetOneOrNull, apiPatch } from '@/core/api/resources'
import { assertWriteIdentity, captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'
import { trackSettingsWrite } from '@/core/api/settingsWrites'
import { updateMeOrganisationName } from './session'

type NameEdit = { name: string; confirmed: string; pending: number; tail: Promise<void> }
const nameEdits = new Map<string, NameEdit>()

export async function getOrganisation(organisationId: string): Promise<Organisation | null> {
  const identity = captureWriteIdentity()
  const key = JSON.stringify([identity, organisationId])
  const beforeRead = nameEdits.get(key)
  const pendingAtRead = (beforeRead?.pending ?? 0) > 0
  const row = await apiGetOneOrNull('/organisation/{id}', { id: organisationId })
  assertWriteIdentity(identity)
  if (!row) return null
  const edit = nameEdits.get(key)
  const name = edit && (pendingAtRead || edit.pending > 0 || edit !== beforeRead) ? edit.name : row.name
  if (!edit || (!pendingAtRead && edit.pending === 0 && edit === beforeRead)) {
    nameEdits.set(key, { name, confirmed: name, pending: 0, tail: Promise.resolve() })
  }
  return { ...row, name }
}

export function updateOrganisation(
  organisationId: string,
  patch: OrganisationPatch,
): Promise<void> {
  return trackSettingsWrite(async (identity) => {
    const key = JSON.stringify([identity, organisationId])
    if (patch.name === undefined) {
      await apiPatch('/organisation/{id}', { id: organisationId }, patch, identity)
      return
    }

    const previous = nameEdits.get(key)
    const edit: NameEdit = {
      name: patch.name,
      confirmed: previous?.confirmed ?? patch.name,
      pending: (previous?.pending ?? 0) + 1,
      tail: Promise.resolve(),
    }
    nameEdits.set(key, edit)
    updateMeOrganisationName(organisationId, edit.name)
    edit.tail = (previous?.tail.catch(() => {}) ?? Promise.resolve()).then(async () => {
      try {
        await apiPatch('/organisation/{id}', { id: organisationId }, patch, identity)
        const latest = nameEdits.get(key)
        if (latest) latest.confirmed = edit.name
      } catch (error) {
        if (nameEdits.get(key) === edit) {
          edit.name = edit.confirmed
          if (isWriteIdentityCurrent(identity)) updateMeOrganisationName(organisationId, edit.confirmed)
        }
        throw error
      } finally {
        const latest = nameEdits.get(key)
        if (latest) latest.pending -= 1
      }
    })
    await edit.tail
  })
}
